import React, { useState, useEffect } from 'react';
import {
  Typography,
  Row,
  Col,
  Card,
  Space,
  Divider,
  Button,
  Table,
  Tag,
  Modal,
  message,
  Spin,
  Alert,
  Tooltip,
  Progress,
  Breadcrumb,
} from 'antd';
import {
  FireOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { getUserProfileWithStripeInfo } from '../auth';
import { api, creditsApi } from '../api';
import { Helmet } from 'react-helmet';
import moment from 'moment-timezone';

const { Title, Text, Paragraph } = Typography;

const SubscriptionManagement = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [stripeInfo, setStripeInfo] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [
        profileWithStripeInfo,
        subscriptionResponse,
        transactionsResponse,
        creditInfoResponse,
      ] = await Promise.all([
        getUserProfileWithStripeInfo(),
        api.get('/subscriptions/current-subscription'),
        api.get('/subscriptions/transactions'),
        creditsApi.getMyCredits(),
      ]);
      setUserProfile(profileWithStripeInfo);
      setStripeInfo({
        last4: profileWithStripeInfo.last4,
        brand: profileWithStripeInfo.brand,
      });
      setCurrentSubscription(subscriptionResponse.data);
      setTransactions(transactionsResponse.data);
      setCreditInfo(creditInfoResponse);
      console.log(currentSubscription);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      message.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await api.post('/subscriptions/cancel-subscription');
      message.success('サブスクリプションをキャンセルしました');
      fetchData();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      message.error('サブスクリプションのキャンセルに失敗しました');
    }
    setCancelModalVisible(false);
  };

  const handleChangePaymentMethod = async () => {
    try {
      const response = await api.post('/subscriptions/create-portal-session');
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Failed to create portal session:', error);
      message.error(
        '決済方法の変更に失敗しました。管理者にお問い合わせください。',
      );
    }
  };

  const planTags = {
    // フリープラン：薄いグレー
    free: { color: 'default', text: 'フリープラン' },

    // 新プラン
    // ライトプラン：青系
    lite: { color: 'blue', text: 'ライトプラン' },
    // スタンダードプラン：緑系
    standard_new: { color: 'success', text: 'スタンダードプラン' },
    // プレミアムプラン：ゴールド
    premium_new: { color: 'warning', text: 'プレミアムプラン' },

    // 旧プラン (Legacy)
    // 旧スタンダードプラン：緑系
    standard: { color: 'success', text: 'スタンダードプラン（Legacy）' },
    // 旧プロプラン：青系
    pro: { color: 'blue', text: 'プロプラン（Legacy）' },
    // 旧プレミアムプラン：ゴールド
    premium: { color: 'warning', text: 'プレミアムプラン（Legacy）' },

    // その他
    // カスタムプラン：パープル系
    custom: { color: 'purple', text: 'カスタムプラン' },
  };

  const planNameMap = {
    free: 'フリープラン',
    // 新プラン
    lite: 'ライトプラン',
    standard_new: 'スタンダードプラン',
    premium_new: 'プレミアムプラン',
    // 旧プラン (Legacy)
    standard: 'スタンダードプラン（Legacy）',
    pro: 'プロプラン（Legacy）',
    premium: 'プレミアムプラン（Legacy）',
    // その他
    custom: 'カスタムプラン',
  };

  const getNextUpdateDate = () => {
    if (!currentSubscription) return 'N/A';

    const nextTicketUpdate = moment.utc(currentSubscription.next_ticket_update);
    const now = moment.utc();

    // フリープランの場合、last_ticket_update_at から30日後に更新
    if (
      currentSubscription.plan.type === 'free' &&
      currentSubscription.last_ticket_update_at
    ) {
      const lastUpdate = moment.utc(currentSubscription.last_ticket_update_at);
      const nextUpdate = lastUpdate.add(30, 'days');
      if (nextUpdate.isAfter(now)) {
        return nextUpdate.local().format('YYYY年MM月DD日');
      }
    }
    // 他のプランの場合
    else if (nextTicketUpdate.isValid() && nextTicketUpdate.isAfter(now)) {
      return nextTicketUpdate.local().format('YYYY年MM月DD日');
    }

    return '更新予定なし';
  };

  const getTicketUpdateCount = () => {
    if (!userProfile || !currentSubscription) return 0;

    if (currentSubscription.plan.type === 'custom') {
      return currentSubscription.custom_data?.is_monthly_update
        ? currentSubscription.custom_data.monthly_tickets
        : 0;
    } else if (currentSubscription.plan.type === 'free') {
      return 0; // フリープランは0枚更新
    } else {
      return currentSubscription.plan.monthly_tickets;
    }
  };

  const isSubscriptionEnding = () => {
    if (!currentSubscription) return false;
    if (currentSubscription.plan.type === 'free') return false;
    return currentSubscription.is_final_update;
  };

  const CancelSubscriptionModal = ({
    visible,
    onCancel,
    onConfirm,
    subscription,
  }) => {
    const isYearly = subscription?.is_yearly;
    const nextRenewalDate = subscription
      ? moment(subscription.current_period_end)
          .tz('Asia/Tokyo')
          .format('YYYY年MM月DD日')
      : 'N/A';

    return (
      <Modal
        visible={visible}
        title={
          <Title level={4} style={{ marginBottom: 0 }}>
            <ExclamationCircleOutlined
              style={{ color: '#faad14', marginRight: 8 }}
            />
            サブスクリプションのキャンセル
          </Title>
        }
        onCancel={onCancel}
        footer={[
          <Button key="back" onClick={onCancel}>
            戻る
          </Button>,
          <Button key="submit" type="primary" danger onClick={onConfirm}>
            キャンセルを確定
          </Button>,
        ]}
        width={600}
      >
        <div style={{ marginTop: '20px' }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              message="キャンセルの影響"
              description={
                <ul style={{ paddingLeft: '20px', marginBottom: 0 }}>
                  <li>現在のプランは{nextRenewalDate}まで利用可能です。</li>
                  <li>
                    {nextRenewalDate}以降は自動的にフリープランに変更されます。
                  </li>
                  <li>フリープランでは機能が制限されます。</li>
                </ul>
              }
              type="warning"
              showIcon
            />

            {isYearly && (
              <Alert
                message="年払いプランに関する重要な注意"
                description={
                  <ul style={{ paddingLeft: '20px', marginBottom: 0 }}>
                    <li>
                      <strong>クレジット使用量について：</strong>{' '}
                      {nextRenewalDate}
                      時点でクレジット使用量がリセットされます。繰り越しはありません。
                    </li>
                    <li>
                      <strong>返金について：</strong>{' '}
                      年払いプランのキャンセルに伴う返金は行っておりません。
                    </li>
                  </ul>
                }
                type="error"
                showIcon
                icon={<WarningOutlined />}
              />
            )}

            <div style={{ marginTop: '10px' }}>
              <Paragraph>
                <Text strong>キャンセル後の動作：</Text>
              </Paragraph>
              <ul style={{ marginTop: '5px', marginBottom: '10px' }}>
                <li>現在のクレジット上限は{nextRenewalDate}まで有効です。</li>
                <li>
                  {nextRenewalDate}
                  以降、クレジット上限がフリープランに変更されます。
                </li>
                <li>
                  フリープランの制限内で引き続きサービスをご利用いただけます。
                </li>
              </ul>
            </div>

            <Divider style={{ margin: '10px 0' }} />

            <Paragraph>
              <Text type="secondary">
                キャンセルを確定する前に、もう一度ご確認ください。キャンセル後も
                {nextRenewalDate}までは現在のプランをご利用いただけます。
              </Text>
            </Paragraph>
          </Space>
        </div>
      </Modal>
    );
  };

  const columns = [
    {
      title: '日付',
      dataIndex: 'created_at',
      key: 'created_at',
      render: date => moment(date).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm'),
    },
    {
      title: '金額（税込）',
      dataIndex: 'amount',
      key: 'amount',
      render: amount => `¥${amount.toLocaleString()}`,
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <Tag color={status === 'succeeded' ? 'green' : 'red'}>
          {status === 'succeeded' ? '成功' : '失敗'}
        </Tag>
      ),
    },
    {
      title: '内容',
      dataIndex: 'plan_name',
      key: 'plan_name',
      render: (planName, record) => {
        // ボーナスクレジット購入の場合
        if (planName && planName.startsWith('bonus_credit_')) {
          const creditAmount = planName.replace('bonus_credit_', '');
          return `ボーナスクレジット購入（${creditAmount}クレジット）`;
        }

        const planType = planNameMap[planName] || planName;
        const transactionType = record.is_new ? '新規' : '継続';
        if (planName === 'custom') {
          return `${planType}`;
        } else if (planName === 'free') {
          return `${planType}`;
        } else {
          const paymentType = record.is_yearly ? '年払い' : '月払い';
          return `${planType}／${paymentType}`;
        }
      },
    },
  ];

  const renderPlanInfo = () => {
    if (!currentSubscription || !currentSubscription.plan) {
      return '情報がありません';
    }

    // プラン名のマッピングを取得
    const planTypeText = (() => {
      // プラン名（name）を優先して使用
      const planName = currentSubscription.plan.name;
      // プランタイプはフォールバックとして使用
      const planType = currentSubscription.plan.type;

      // プラン名のマッピングを確認
      if (planNameMap[planName]) {
        return planNameMap[planName];
      }
      // フォールバック: プランタイプのマッピング
      else if (planNameMap[planType]) {
        return planNameMap[planType];
      }
      // 最終フォールバック: 表示名
      return currentSubscription.plan.display_name || 'unknown';
    })();

    const planColor = (() => {
      const planName = currentSubscription.plan.name;
      const planType = currentSubscription.plan.type;

      if (planTags[planName]) {
        return planTags[planName].color;
      } else if (planTags[planType]) {
        return planTags[planType].color;
      }
      return 'blue'; // デフォルト色
    })();

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Tag
            color={planColor}
            style={{ padding: '4px 8px', fontSize: '14px' }}
          >
            {planTypeText}
            {currentSubscription.plan.type !== 'free' &&
              currentSubscription.plan.type !== 'custom' &&
              `（${currentSubscription.is_yearly ? '年払い' : '月払い'}）`}
          </Tag>
        </Col>
      </Row>
    );
  };

  if (loading) {
    return <Spin size="large" />;
  }

  return (
    <>
      <Helmet>
        <title>サブスクリプション管理 | SEO記事生成モード - magicss`</title>
        <meta name="robots" content="noindex" />
        <meta
          property="og:title"
          content={`サブスクリプション管理 | SEO記事生成モード - magicss`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`${process.env.PUBLIC_URL}/img/ogp.png`}
        />
        <meta
          property="og:description"
          content={`コンテンツ生成AIツール「magicss」のサブスクリプション管理ページです。`}
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div>
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[
            { title: <Link to="/mypage">マイページ</Link> },
            { title: 'サブスクリプション管理' },
          ]}
        />
        <Title
          level={1}
          className="gradient-text"
          style={{ fontSize: '24px', marginBottom: 8 }}
        >
          サブスクリプション管理
        </Title>
        <Text type="secondary">
          次回更新日やクレジットリセット日、決済方法の変更などサブスクリプションの管理を行なえます。
        </Text>
        <Divider style={{ margin: '24px 0' }} />

        <Row gutter={[16, 16]}>
          {/* 左カラム: クレジット利用状況 */}
          <Col xs={24} lg={12}>
            <Card title="クレジット利用状況" style={{ height: '100%' }}>
              {creditInfo ? (
                <>
                  <Space
                    direction="vertical"
                    size="middle"
                    style={{ width: '100%' }}
                  >
                    {/* 使用状況のプログレスバー */}
                    <div>
                      <div
                        style={{
                          marginBottom: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Text strong>使用状況</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {Math.round((creditInfo.usage_rate || 0) * 100)}%
                        </Text>
                      </div>
                      <Progress
                        percent={Math.round((creditInfo.usage_rate || 0) * 100)}
                        strokeColor={{
                          '0%': '#52c41a',
                          '70%': '#faad14',
                          '90%': '#ff4d4f',
                        }}
                        showInfo={false}
                      />
                    </div>

                    <Divider style={{ margin: '8px 0' }} />

                    {/* クレジット数値表示 */}
                    <div>
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: '100%' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Text type="secondary">使用済み</Text>
                          <Text
                            strong
                            style={{ fontSize: '16px', color: '#0072ff' }}
                          >
                            {creditInfo.credit_used.toLocaleString()}
                          </Text>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Text type="secondary">上限</Text>
                          <Text strong style={{ fontSize: '16px' }}>
                            {creditInfo.plan_limit === 999999999
                              ? '∞'
                              : creditInfo.plan_limit.toLocaleString()}
                          </Text>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Text type="secondary">残り</Text>
                          <Text
                            strong
                            style={{ fontSize: '16px', color: '#52c41a' }}
                          >
                            {creditInfo.plan_limit === 999999999
                              ? '∞'
                              : creditInfo.available_regular.toLocaleString()}
                          </Text>
                        </div>
                        {creditInfo.bonus_credit > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Tooltip title="通常クレジット使用後に利用される無期限クレジット">
                              <Text type="secondary">
                                ボーナス{' '}
                                <FireOutlined style={{ fontSize: '12px' }} />
                              </Text>
                            </Tooltip>
                            <Text
                              strong
                              style={{
                                fontSize: '16px',
                                background:
                                  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                              }}
                            >
                              +{creditInfo.bonus_credit.toLocaleString()}
                            </Text>
                          </div>
                        )}
                      </Space>
                    </div>

                    <Divider style={{ margin: '8px 0' }} />

                    {/* 次回更新日 */}
                    <div>
                      {creditInfo.next_reset_date ? (
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          次回クレジットリセット:{' '}
                          {moment(creditInfo.next_reset_date)
                            .tz('Asia/Tokyo')
                            .format('YYYY年MM月DD日')}
                          {creditInfo.days_until_reset > 0 &&
                            ` (残り${creditInfo.days_until_reset}日)`}
                        </Text>
                      ) : (
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                          更新なし（管理者アカウント）
                        </Text>
                      )}
                    </div>
                  </Space>
                </>
              ) : (
                <Spin size="small" />
              )}
            </Card>
          </Col>

          {/* 右カラム: プラン情報＆決済方法 */}
          <Col xs={24} lg={12}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {/* 現在のプラン */}
              <Card
                title="現在のプラン"
                extra={
                  <Button
                    type="default"
                    size="small"
                    onClick={() => navigate('/plan')}
                    style={{
                      background: '#fff',
                      borderColor: '#d9d9d9',
                    }}
                  >
                    プラン変更はこちら
                  </Button>
                }
              >
                {currentSubscription && (
                  <>
                    {renderPlanInfo()}
                    <Divider />
                    {currentSubscription.plan.type !== 'free' && (
                      <>
                        <Text>
                          {currentSubscription.plan.type === 'custom'
                            ? '有効期限'
                            : '次回プラン更新日'}
                          ：
                          {moment(currentSubscription.current_period_end)
                            .tz('Asia/Tokyo')
                            .format('YYYY年MM月DD日')}
                        </Text>
                        {currentSubscription.status === 'canceling' && (
                          <Text type="warning" style={{ marginLeft: '8px' }}>
                            （自動キャンセル）
                          </Text>
                        )}
                        {currentSubscription.status === 'trialing' && (
                          <div style={{ marginTop: 8 }}>
                            <Alert
                              type="info"
                              showIcon
                              message={
                                <span>
                                  14日間無料トライアル中です。
                                  {currentSubscription.trial_info && (
                                    <> 残り <strong>{currentSubscription.trial_info.remaining_days}</strong> 日</>
                                  )}
                                  　期間終了後は自動的に有料プランに移行します。
                                </span>
                              }
                              style={{ marginTop: 4 }}
                            />
                          </div>
                        )}
                      </>
                    )}
                    {currentSubscription.plan.type === 'free' && (
                      <Text>フリープランには有効期限はありません。</Text>
                    )}
                  </>
                )}
              </Card>

              {/* 決済方法 */}
              <Card title="決済方法">
                {userProfile && (
                  <>
                    <div
                      style={{
                        background: 'linear-gradient(45deg, #e5f3ff, #ffeadc)',
                        borderRadius: '10px',
                        padding: '10px',
                        textAlign: 'center',
                        marginBottom: '16px',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: '14px',
                          marginRight: '16px',
                          color: '#647a7a',
                        }}
                      >
                        {stripeInfo.brand
                          ? `${stripeInfo.brand.toUpperCase()}`
                          : 'カード'}
                      </Text>
                      <Text
                        style={{
                          fontSize: '18px',
                          letterSpacing: '2px',
                          color: '#647a7a',
                        }}
                      >
                        {stripeInfo.last4
                          ? `**** **** **** ${stripeInfo.last4}`
                          : '未登録'}
                      </Text>
                    </div>
                    <Button onClick={handleChangePaymentMethod} block>
                      決済方法の変更・履歴の確認
                    </Button>
                  </>
                )}
              </Card>
            </Space>
          </Col>
        </Row>

        <Title level={3} style={{ margin: '32px 0 16px', fontSize: '18px' }}>
          決済履歴
        </Title>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          scroll={{ x: 'max-content' }}
        />

        <CancelSubscriptionModal
          visible={cancelModalVisible}
          onCancel={() => setCancelModalVisible(false)}
          onConfirm={handleCancelSubscription}
          subscription={currentSubscription}
        />
      </div>
    </>
  );
};

export default SubscriptionManagement;
